/**
 * Owner/admin API calls (WP-FE-7).
 *
 * Every route here except login is owner-only (contract v1 §2, §3.8). The
 * apiClient attaches the token from sessionStorage 'hh_admin_token' on
 * these paths and, on a 401, clears it and fires 'hh:unauthorized'.
 *
 * Deliberately NOT exported from the public services/api barrel: the public
 * site has no reason to import owner calls.
 *
 * Datetimes sent to the backend always carry 'Z' (admin routes reject
 * naive datetimes, contract §1.3).
 *
 * @module adminService
 */

import { get, post, patch, del, setAdminToken, clearAdminToken, getAdminToken } from './apiClient';
import ApiError, { ERROR_CODES } from '../errors/ApiError';
import { ERROR_MESSAGES } from '../errors/errorHandler';

/** Booking statuses (backend BOOKING_STATUSES). */
export const BOOKING_STATUS = Object.freeze({
  SCHEDULED: 'scheduled',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  NO_SHOW: 'no_show',
  CANCELLED: 'cancelled',
});

/**
 * Booking requests waiting for the owner (contract v1.3 §3.4, A.14): every
 * online booking starts 'scheduled' until she confirms or declines it.
 */
export const REQUEST_STATUS = BOOKING_STATUS.SCHEDULED;

/**
 * Answered bookings that are not cancelled: what the Upcoming and Past tabs
 * list. Requests ('scheduled') have their own "Waiting for you" list.
 */
export const ANSWERED_STATUSES = Object.freeze([
  BOOKING_STATUS.CONFIRMED,
  BOOKING_STATUS.COMPLETED,
  BOOKING_STATUS.NO_SHOW,
]);

/** Statuses that can still be cancelled, completed or marked no-show. */
export const ACTIVE_STATUSES = Object.freeze([BOOKING_STATUS.SCHEDULED, BOOKING_STATUS.CONFIRMED]);

/** Everything except cancelled. */
export const NOT_CANCELLED_STATUSES = Object.freeze([
  BOOKING_STATUS.SCHEDULED,
  BOOKING_STATUS.CONFIRMED,
  BOOKING_STATUS.COMPLETED,
  BOOKING_STATUS.NO_SHOW,
]);

/** Largest page GET /api/bookings returns (backend MAX_PAGE_SIZE). */
export const BOOKINGS_PAGE_SIZE = 100;

/** Longest cancellation reason the backend accepts. */
export const MAX_CANCELLATION_REASON_LENGTH = 2000;

/** Longest decline message the backend accepts (A.14). */
export const MAX_DECLINE_REASON_LENGTH = 2000;

/** 'Z' form of an instant, e.g. '2026-10-05T13:30:00Z'. */
export const toUtcIso = (date) => new Date(date).toISOString().replace(/\.\d{3}Z$/, 'Z');

/** @returns {boolean} True when an owner token is stored. */
export const isAuthenticated = () => Boolean(getAdminToken());

/**
 * Sign the owner in. Stores the JWT in sessionStorage 'hh_admin_token'.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{expiresIn: number|null}>}
 * @throws {ApiError} 400 validation_error, 401 unauthorized, 429, 503 service_unavailable
 */
export const login = async (email, password) => {
  const data = await post('/api/auth/login', {
    email: String(email || '').trim(),
    password: String(password || ''),
  });
  if (!data || typeof data.access_token !== 'string' || !data.access_token) {
    throw new ApiError(ERROR_MESSAGES[ERROR_CODES.INVALID_RESPONSE], 200, ERROR_CODES.INVALID_RESPONSE);
  }
  setAdminToken(data.access_token);
  return { expiresIn: typeof data.expires_in === 'number' ? data.expires_in : null };
};

/** Forget the owner token (there is no server-side session to end). */
export const logout = () => {
  clearAdminToken();
};

/**
 * Check the stored token is still accepted.
 * @returns {Promise<{role: string}>}
 */
export const getSession = ({ signal } = {}) => get('/api/auth/me', { signal });

/**
 * List bookings (owner).
 *
 * @param {object} [filters]
 * @param {string|string[]} [filters.status] - one status or several
 * @param {string|Date} [filters.from] - start-time lower bound (sent as 'Z')
 * @param {string|Date} [filters.to] - start-time upper bound (sent as 'Z')
 * @param {number} [filters.limit] - 1..100
 * @param {number} [filters.offset]
 * @param {'asc'|'desc'} [filters.order] - by start time; the API default is
 *   'desc' (newest start first). Upcoming asks for 'asc' so its first page
 *   holds the soonest sessions (contract Appendix A, v1.1).
 * @param {AbortSignal} [filters.signal]
 * @returns {Promise<{bookings: object[], total: number, limit: number, offset: number}>}
 */
export const listBookings = async ({
  status,
  from,
  to,
  limit = BOOKINGS_PAGE_SIZE,
  offset = 0,
  order,
  signal,
} = {}) => {
  const params = { limit, offset };
  if (order === 'asc' || order === 'desc') params.order = order;
  if (status && status.length) params.status = Array.isArray(status) ? status.join(',') : status;
  if (from) params.from = toUtcIso(from);
  if (to) params.to = toUtcIso(to);

  const data = await get('/api/bookings', { params, signal });
  const bookings = Array.isArray(data.bookings) ? data.bookings : [];
  return {
    bookings,
    total: typeof data.total === 'number' ? data.total : bookings.length,
    limit: typeof data.limit === 'number' ? data.limit : limit,
    offset: typeof data.offset === 'number' ? data.offset : offset,
  };
};

const bookingPath = (id, action = '') => `/api/bookings/${encodeURIComponent(id)}${action}`;

/**
 * Get one booking (owner).
 * @param {number} id - internal booking id
 * @returns {Promise<object>} admin booking
 */
export const getBooking = (id, { signal } = {}) => get(bookingPath(id), { signal });

/**
 * Who asked for a cancellation (DELETE /api/bookings/<id> body cancelled_by).
 * Only a CLIENT cancellation inside the cancellation window owes the late fee.
 * Admin bookings may also say 'calendar_sync' (event deleted in Google) or,
 * on older rows, 'owner'.
 */
export const CANCELLED_BY = Object.freeze({
  CLIENT: 'client',
  PRACTICE: 'practice',
});

/**
 * Cancel a booking.
 * @param {number} id
 * @param {object} options
 * @param {'client'|'practice'} options.cancelledBy - who asked for it
 * @param {string} [options.reason] - kept on the booking
 * @returns {Promise<object>} updated admin booking
 * @throws {ApiError} 400 validation_error, 404 not_found
 */
export const cancelBooking = (id, { cancelledBy, reason } = {}) => {
  if (!Object.values(CANCELLED_BY).includes(cancelledBy)) {
    throw new Error("cancelledBy must be 'client' or 'practice'");
  }
  const trimmed = String(reason || '').trim();
  const data = { cancelled_by: cancelledBy };
  if (trimmed) data.reason = trimmed;
  return del(bookingPath(id), { data });
};

/**
 * Confirm a booking request (A.14). Only from 'scheduled'; confirming an
 * already confirmed booking is a no-op 200. After the commit the backend
 * pushes the calendar event and emails the client the confirmation (join
 * link or "the link will follow", .ics invite).
 * @param {number} id
 * @returns {Promise<object>} updated admin booking (status 'confirmed')
 * @throws {ApiError} 404 not_found, 409 invalid_state (already cancelled,
 *   or its start time has passed)
 */
export const confirmBooking = (id) => post(bookingPath(id, '/confirm'), {});

/**
 * Decline a booking request (A.14). Only from 'scheduled'; the booking
 * becomes cancelled by the practice, its slot is freed and the client is
 * emailed (with `reason` when given). No late-cancel fee ever applies.
 * @param {number} id
 * @param {string} [reason] - optional message to the client, at most 2000 characters
 * @returns {Promise<object>} updated admin booking (status 'cancelled')
 * @throws {ApiError} 400 validation_error, 404 not_found, 409 invalid_state
 */
export const declineBooking = (id, reason) => {
  const trimmed = String(reason || '').trim();
  return post(bookingPath(id, '/decline'), trimmed ? { reason: trimmed } : {});
};

/**
 * Mark a booking completed. Only once the session has started (409
 * invalid_state before that).
 * @param {number} id
 * @returns {Promise<object>} updated admin booking
 */
export const completeBooking = (id) => post(bookingPath(id, '/complete'), {});

/**
 * Mark a booking as a no-show. Only once the session has started (409
 * invalid_state before that).
 * @param {number} id
 * @returns {Promise<object>} updated admin booking
 */
export const markNoShow = (id) => post(bookingPath(id, '/no-show'), {});

/**
 * Push the booking to Google Calendar again. Resolves with the booking and
 * its recorded sync state even when Google is still down.
 * @param {number} id
 * @returns {Promise<object>} updated admin booking
 */
export const retryCalendarSync = (id) => post(bookingPath(id, '/retry-calendar-sync'), {});

/**
 * Set the session join link. The backend emails the client "Your session
 * link" once per new link (meeting_link_sent_at records it; it stays null
 * while email is off).
 * @param {number} id
 * @param {string} meetingUrl - https:// link, at most 512 characters
 * @returns {Promise<object>} updated admin booking
 * @throws {ApiError} 400 validation_error, 404 not_found, 409 invalid_state
 */
export const setMeetingUrl = (id, meetingUrl) =>
  patch(bookingPath(id, '/meeting-url'), { meeting_url: String(meetingUrl || '').trim() });

// ---------------------------------------------------------------------------
// Email outbox (GET /api/admin/notifications)
// ---------------------------------------------------------------------------

/** Outbox row statuses. failed = will retry by itself; dead = gave up. */
export const EMAIL_STATUS = Object.freeze({
  PENDING: 'pending',
  SENT: 'sent',
  FAILED: 'failed',
  DEAD: 'dead',
});

const pageOf = (data, key, limit, offset) => {
  const rows = Array.isArray(data && data[key]) ? data[key] : [];
  return {
    rows,
    total: typeof data?.total === 'number' ? data.total : rows.length,
    limit: typeof data?.limit === 'number' ? data.limit : limit,
    offset: typeof data?.offset === 'number' ? data.offset : offset,
  };
};

/**
 * Stored emails by status (default failed + dead), newest first. Bodies
 * are never returned.
 * @param {object} [options]
 * @param {string|string[]} [options.status] - pending|sent|failed|dead
 * @param {number} [options.limit] - 1..100 (default 50)
 * @param {number} [options.offset]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{counts: {pending:number, sent:number, failed:number, dead:number}, notifications: object[], total: number, limit: number, offset: number}>}
 */
export const listNotifications = async ({ status, limit = 50, offset = 0, signal } = {}) => {
  const params = { limit, offset };
  if (status && status.length) params.status = Array.isArray(status) ? status.join(',') : status;
  const data = await get('/api/admin/notifications', { params, signal });
  const page = pageOf(data, 'notifications', limit, offset);
  const counts = data && typeof data.counts === 'object' && data.counts ? data.counts : {};
  return {
    counts: {
      pending: Number(counts.pending) || 0,
      sent: Number(counts.sent) || 0,
      failed: Number(counts.failed) || 0,
      dead: Number(counts.dead) || 0,
    },
    notifications: page.rows,
    total: page.total,
    limit: page.limit,
    offset: page.offset,
  };
};

/**
 * Try one failed/dead email again now.
 * @param {number} id - outbox row id
 * @returns {Promise<object>} the row after the attempt (status sent, or still failed/dead)
 * @throws {ApiError} 404 not_found, 409 invalid_state (already sent),
 *   503 service_unavailable (email off or not configured)
 */
export const retryNotification = (id) =>
  post(`/api/admin/notifications/${encodeURIComponent(id)}/retry`, {});

// ---------------------------------------------------------------------------
// Contact messages (GET /api/admin/contact-messages)
// ---------------------------------------------------------------------------

/**
 * Contact-form messages, newest first.
 * @param {object} [options]
 * @param {boolean} [options.handled] - true: handled only; false: still to do; omit: all
 * @param {number} [options.limit] - 1..100 (default 50)
 * @param {number} [options.offset]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{messages: object[], total: number, unhandled: number, limit: number, offset: number}>}
 */
export const listContactMessages = async ({ handled, limit = 50, offset = 0, signal } = {}) => {
  const params = { limit, offset };
  if (typeof handled === 'boolean') params.handled = String(handled);
  const data = await get('/api/admin/contact-messages', { params, signal });
  const page = pageOf(data, 'messages', limit, offset);
  return {
    messages: page.rows,
    total: page.total,
    unhandled: typeof data?.unhandled === 'number' ? data.unhandled : 0,
    limit: page.limit,
    offset: page.offset,
  };
};

/**
 * Mark a contact message handled (idempotent: the first time is kept).
 * @param {number} id
 * @returns {Promise<object>} the message with handled_at set
 * @throws {ApiError} 404 not_found
 */
export const markContactMessageHandled = (id) =>
  post(`/api/admin/contact-messages/${encodeURIComponent(id)}/handled`, {});
